import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import {
  AppLink,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  TableRowLink,
} from "@sagot/ui";
import { useNavigate } from "react-router";
import { formatBytes, formatDate } from "../shared/format";
import { isProcessing } from "../shared/ingestion";
import type { SourceView } from "../shared/types";
import { AccessBadge, PiiChip } from "./access-badges";
import { sourceIcon, typeOf } from "./source-model";
import { SourceStatus } from "./source-status";

export interface SourceRowAbilities {
  reindex: boolean;
  edit: boolean;
  remove: boolean;
}

export interface SourceRowActions {
  onReindex: (source: SourceView) => void;
  onChangeAccess: (source: SourceView) => void;
  onDelete: (source: SourceView) => void;
}

interface SourceRowProps extends SourceRowActions {
  source: SourceView;
  workspaceSlug: string;
  agentId: string | null;
  can: SourceRowAbilities;
}

/** Detail link; `?agent=` lets the detail page link back to this agent's Knowledge Base. */
export function detailHref(workspaceSlug: string, sourceId: string, agentId: string | null): string {
  const base = consoleRoutes.sourceDetail(workspaceSlug, sourceId);
  return agentId ? `${base}?agent=${encodeURIComponent(agentId)}` : base;
}

function useMeta(source: SourceView): string {
  const { t } = useTranslation();
  if (source.type === "website") return source.url ?? "";
  const parts: string[] = [];
  if (source.page_count) parts.push(t("deployKnowledge.knowledge.table.pages", { count: source.page_count }));
  if (source.size_bytes) parts.push(formatBytes(source.size_bytes));
  if (source.version > 1) parts.push(t("deployKnowledge.knowledge.table.version", { version: source.version }));
  return parts.join(" · ");
}

function RowMenu({ source, workspaceSlug, agentId, can, onReindex, onChangeAccess, onDelete }: SourceRowProps) {
  const { t } = useTranslation();
  const k = "deployKnowledge.knowledge.menu";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" iconOnly aria-label={t(`${k}.label`, { name: source.title })}>
          <Icon name="more-horizontal" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem asChild>
          <AppLink href={detailHref(workspaceSlug, source.id, agentId)}>
            <Icon name="eye" className="size-4" />
            {t(`${k}.view`)}
          </AppLink>
        </DropdownMenuItem>
        {can.reindex ? (
          <DropdownMenuItem disabled={isProcessing(source.status)} onSelect={() => { onReindex(source); }}>
            <Icon name="refresh-cw" className="size-4" />
            {t(`${k}.reindex`)}
          </DropdownMenuItem>
        ) : null}
        {can.edit ? (
          <DropdownMenuItem onSelect={() => { onChangeAccess(source); }}>
            <Icon name="shield-check" className="size-4" />
            {t(`${k}.access`)}
          </DropdownMenuItem>
        ) : null}
        {can.remove ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem tone="danger" onSelect={() => { onDelete(source); }}>
              <Icon name="trash" className="size-4" />
              {t(`${k}.delete`)}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** One sources-table row: whole-row click opens the detail; the name is the keyboard link. */
export function SourceRow(props: SourceRowProps) {
  const { source, workspaceSlug, agentId } = props;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const meta = useMeta(source);
  const tile = sourceIcon(source);
  const href = detailHref(workspaceSlug, source.id, agentId);
  const processing = isProcessing(source.status);
  return (
    <TableRowLink onNavigate={() => void navigate(href)} data-testid="source-row">
      <td>
        <div className="flex items-center gap-2.5">
          <span className={`inline-flex size-8 shrink-0 items-center justify-center rounded-md ${tile.tile}`}>
            <Icon name={tile.icon} className="size-4" />
          </span>
          <div className="min-w-0">
            <AppLink href={href} className="block max-w-72 truncate font-semibold text-fg hover:text-primary-ink">
              {source.title}
            </AppLink>
            {meta ? <div className={`truncate text-[11px] text-fg-subtle ${source.type === "website" ? "font-mono" : ""}`}>{meta}</div> : null}
          </div>
        </div>
      </td>
      <td className="text-fg-muted">{t(`deployKnowledge.knowledge.types.${typeOf(source)}`)}</td>
      <td>
        <div className="flex flex-wrap items-center gap-1.5">
          <AccessBadge audiences={source.audiences} />
          <PiiChip source={source} />
        </div>
      </td>
      <td>
        <SourceStatus source={source} detailsHref={href} />
      </td>
      <td className={`text-right tabular-nums ${processing ? "text-fg-subtle" : ""}`}>{processing && source.chunk_count === 0 ? "—" : source.chunk_count}</td>
      <td className="whitespace-nowrap text-fg-subtle">{formatDate(source.updated_at, i18n.language)}</td>
      <td className="w-10">
        <RowMenu {...props} />
      </td>
    </TableRowLink>
  );
}
